/**
 * Command-web Terminal Supervisor helpers (#444).
 *
 * Pure interpretation and self-heal planning over existing terminal-attachment
 * process/transport truth. Does not open a second PTY or transport.
 */

import {
  automationMayWrite,
  findingFromAnomaly,
  interpretTerminalTelemetry,
  ownerKeyboardPriorityActive,
  planTerminalSelfHeal,
  planTransportRecover,
  processTruthFromStatus,
  shouldEmitTerminalFinding,
  transportTruthFromState,
  type TerminalSelfHealPlan,
  type TerminalSupervisorAnomaly,
  type TerminalSupervisorFinding,
  type TerminalSupervisorTelemetry,
  OWNER_KEYSTROKE_HOLD_SECONDS,
} from '@brunel/shared';
import type { TerminalTransportState } from './terminal-attachment';

export {
  automationMayWrite,
  ownerKeyboardPriorityActive,
  shouldEmitTerminalFinding,
  OWNER_KEYSTROKE_HOLD_SECONDS,
};

export interface CommandTerminalWatchInput {
  readonly sessionId: string | null;
  readonly hasTerminal: boolean;
  readonly status: string | null;
  readonly closedAt: string | null;
  readonly transport: TerminalTransportState;
  readonly inputHeld: boolean;
  readonly inputHolder: string | null;
  readonly heldUntilIso?: string | null;
  readonly latestSeq: number;
  readonly highestRenderedSeq: number;
  readonly hasSequenceGap: boolean;
  readonly ptyAlive: boolean;
  readonly reconnectAttempt: number;
  readonly heldRefusalCount?: number;
  readonly nowMs: number;
}

export function watchTelemetryFromCommand(input: CommandTerminalWatchInput): TerminalSupervisorTelemetry {
  return {
    sessionId: input.sessionId,
    hasTerminal: input.hasTerminal,
    process: processTruthFromStatus(input.hasTerminal, input.status, input.closedAt),
    transport: transportTruthFromState(input.hasTerminal, input.transport),
    inputHeld: input.inputHeld,
    inputHolder: input.inputHolder,
    heldUntilIso: input.heldUntilIso ?? null,
    latestSeq: input.latestSeq,
    highestRenderedSeq: input.highestRenderedSeq,
    hasSequenceGap: input.hasSequenceGap,
    ptyAlive: input.ptyAlive,
    reconnectAttempt: input.reconnectAttempt,
    heldRefusalCount: input.heldRefusalCount ?? 0,
    nowMs: input.nowMs,
  };
}

export function superviseCommandTerminal(input: CommandTerminalWatchInput): {
  readonly anomalies: readonly TerminalSupervisorAnomaly[];
  readonly findings: readonly TerminalSupervisorFinding[];
  readonly heals: readonly TerminalSelfHealPlan[];
  readonly transportRecover: TerminalSelfHealPlan;
} {
  const telemetry = watchTelemetryFromCommand(input);
  const anomalies = interpretTerminalTelemetry(telemetry);
  const findings = anomalies.map(findingFromAnomaly);
  const heals = anomalies.map((anomaly) => planTerminalSelfHeal(anomaly, telemetry));
  return {
    anomalies,
    findings,
    heals,
    transportRecover: planTransportRecover(telemetry),
  };
}
